/* global i18n */

import React, { useEffect } from 'react'
import { compose, values, flatten, length, filter, either, equals, path } from 'ramda'
import { isNotEmpty, isObject } from 'ramda-adjunct'
import { hot, setConfig } from 'react-hot-loader'
import { useSelector, useDispatch } from 'react-redux'
import { If, Then, Else, When } from 'react-if'
import { xhrGet } from '../../helpers/http.js'
import Title from '../../components/Title/Title.jsx'
import LoadingText from '../../components/LoadingText/LoadingText.jsx'
import { baseUrl } from '../../common/constants.js'
import Items from './components/Items/Items.jsx'
import Total from './components/Total/Total.jsx'
import AbortPayment from './components/AbortPayment/AbortPayment.jsx'
import ClearCart from './components/ClearCart/ClearCart.jsx'
import BankPay from './components/BankPay/BankPay.jsx'
import CryptoPay from './components/CryptoPay/CryptoPay.jsx'
import BonusPay from './components/BonusPay/BonusPay.jsx'
import VoucherPay from './components/VoucherPay/VoucherPay.jsx'
import s from './style.module.scss'

setConfig({
  reloadHooks: false
})

const enhance = compose(hot(module))

const Cart = ({ variation }) => {
  /* const mode = window.__initial_data__.mode */

  const apiGetSettings = async () => {
    return xhrGet(`${baseUrl}/web/cart/settings`)
  }

  const apiGetCart = async () => {
    if (variation === 'history') {
      return xhrGet(`${baseUrl}/web/cart/history`)
    } else {
      return xhrGet(`${baseUrl}/web/cart`)
    }
  }

  const dispatch = useDispatch()

  const isLoading = useSelector((state) => state.Cart.isLoading)
  const [paymentType, setPaymentType] = React.useState('3')
  /* const [paymentInProgress, setInProgress] = React.useState(false) */

  const setIsLoading = (isLoading) => {
    dispatch({
      type: 'Cart.setIsLoading',
      payload: {
        isLoading
      }
    })
  }

  const setSettings = (settings) => {
    dispatch({
      type: 'Cart.setSettings',
      payload: {
        settings
      }
    })
  }

  const items = useSelector((state) => state.Cart.items)

  const setItems = (items) => {
    if (items && items.cart && items.cart.ptype && isNotEmpty(items.cart.ptype)) {
      setPaymentType(items.cart.ptype)
      const select = document.getElementById('payment-selection')
      if (isObject(select)) {
        console.log(select)
        select.value = items.cart.ptype
        select.dispatchEvent(new Event('change', { bubbles: true }))
        select.dispatchEvent(new Event('input', { bubbles: true }))
      }

      if (items?.cart?.data) {
        populateFormFromObject('paymentform_' + items.cart.ptype, items.cart.data)
      }
    }

    if (parseInt(items.cart.status) > 1) {
      setIsUnderPayment(true)
    }
    dispatch({
      type: 'Cart.setItems',
      payload: {
        items
      }
    })
  }
  const setIsUnderPayment = (isUnderPayment) => {
    dispatch({
      type: 'Cart.setIsUnderPayment',
      payload: {
        isUnderPayment
      }
    })
  }

  function populateFormFromObject(formid, dataObject) {
    const form = document.getElementById(formid)

    if (!dataObject || typeof dataObject !== 'object') {
      return false
    }

    if (!form) {
      return false
    }

    // Проходим по ключам объекта
    Object.keys(dataObject).forEach((key) => {
      const value = dataObject[key]

      if (key === undefined || value === undefined) {
        return
      }

      // Ищем все возможные элементы с таким name
      const elements = form.querySelectorAll(`[name="${key}"]`)

      elements.forEach((element) => {
        // Обработка разных типов элементов
        switch (element.tagName.toLowerCase()) {
          case 'input':
            if (element.type === 'checkbox' || element.type === 'radio') {
              element.checked = value === true || value === 'on' || value === '1'
            } else {
              element.value = value
            }
            break

          case 'textarea':
          case 'select':
            element.value = value
            break

          default:
            element.value = value
        }

        // Создаем событие для активации обработчиков
        element.dispatchEvent(new Event('change', { bubbles: true }))
        element.dispatchEvent(new Event('input', { bubbles: true }))
      })
    })

    return true
  }

  const loadSettings = async () => {
    setIsLoading(true)
    try {
      const settings = await apiGetSettings()
      setSettings(settings)
    } catch (e) {
      console.error('Cart: loadSettings failed', await e.json())
    }
    setIsLoading(false)
  }

  const loadItems = async () => {
    setIsLoading(true)
    try {
      const items = await apiGetCart()
      setItems(items)
    } catch (e) {
      console.error('Cart: loadItems failed', e)
    }
    setIsLoading(false)
  }

  // Вместо useEffectOnce используйте:
  useEffect(() => {
    const loadData = async () => {
      await loadSettings()
      await loadItems()
    }

    loadData()
  }, []) // Пустой массив = только при монтировании

  const flatItemList = flatten(values(items))
  const numberOfItems = length(flatItemList)

  const changesAreDue = compose(
    isNotEmpty,
    filter(compose(either(equals('ready to pay'), equals('payment pending')), path(['commerceinfo', 'statusText'])))
  )(flatItemList)

  const isUnderPayment = useSelector((state) => state.Cart.isUnderPayment)

  useEffect(() => {
    if (changesAreDue) {
      const timer = setInterval(async () => {
        const items = await apiGetCart()
        setItems(items)
      }, 3000)

      return () => {
        clearInterval(timer)
      }
    }
  }, [changesAreDue])

  useEffect(() => {
    if (variation !== 'history') {
      const cartNum = document.getElementById('cartnum')
      if (flatItemList.length === 0) {
        cartNum.style.visibility = 'hidden'
      } else {
        cartNum.style.visibility = 'visible'
        cartNum.textContent = flatItemList.length
      }
    }
  }, [flatItemList.length])

  return (
    <>
      <Title>{i18n.t('cart_my_orders')}</Title>

      <LoadingText visible={isLoading} />

      <If condition={numberOfItems > 0}>
        <Then>
          {() => {
            return (
              <>
                <When condition={variation !== 'history'}>
                  <div className={s.topButtons}>
                    <ClearCart />
                    <br />
                  </div>
                </When>

                <Items items={items.photos} variation="photo" isHistory={variation === 'history'} />

                <When condition={variation !== 'history'}>
                  <Total paymentType={paymentType} />

                  <div className={s.paymentSelection}>
                    {!(isLoading || isUnderPayment) && (
                      <label className={s.paymentSelectionSelect}>
                        {i18n.t('cart_select_payment_method')}
                        <select
                          className="form-control"
                          id="payment-selection"
                          value={paymentType}
                          onChange={(event) => {
                            setPaymentType(event.target.value)
                          }}
                        >
                          <option value="1">Pay from balance</option>
                          <option value="2">Pay by voucher</option>
                          <option value="3">Pay by Bank</option>
                          <option value="4">Pay by Crypto</option>
                        </select>
                      </label>
                    )}
                  </div>
                  <AbortPayment style={{ float: 'right' }} />
                  {paymentType === '4' ? <CryptoPay /> : null}

                  {paymentType === '2' ? <VoucherPay /> : null}

                  {paymentType === '3' ? <BankPay /> : null}

                  {paymentType === '1' ? <BonusPay /> : null}
                </When>
              </>
            )
          }}
        </Then>
        <Else>
          <p className="center">{i18n.t(variation === 'history' ? 'purchase_history_empty' : 'cart_empty')}</p>
        </Else>
      </If>
    </>
  )
}

export default enhance(Cart)
